<template>
  <div class="zone-visited widget__content-center widget__content-center--flex">
    <template v-if="area?.name === 'Site' && siteData">
      <div class="chart-wrapper">
        <apexchart
          width="100%"
          height="100%"
          type="treemap"
          :options="chartOptions"
          :series="siteData"
        />
      </div>
    </template>

    <template v-if="area?.name === 'Cluster' && clusterData">
      <div class="widget__label widget__label-thin">
        Percent split of engaged visits across zones in the
        <span class="widget-message__yellow">{{ cluster?.title }}</span> cluster
      </div>

      <div class="chart-wrapper">
        <apexchart
          height="100%"
          width="100%"
          type="treemap"
          :options="chartOptions"
          :series="clusterData"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import type { WidgetFilter, WidgetSetting, WidgetTypeZonesVisited } from '@/types/Widget';
import { mekkoColors, mekkoTextColors } from '@/utils/chartColors';

import type { BaseChartOptions } from '@/utils/chartSettings';
import { getChartExportSettings, getDefaultChartOptions } from '@/utils/chartSettings';
import { formatNumber } from '@/utils/formatNumbers';

const props = defineProps<{
  widget: WidgetTypeZonesVisited;
  area?: WidgetFilter;
  time?: WidgetFilter;
  zone?: WidgetSetting;
  cluster?: WidgetSetting;
}>();

const defaultChartOptions = getDefaultChartOptions(props.widget.code_name);

const chartOptions = ref<BaseChartOptions>({
  ...defaultChartOptions.value,
  chart: {
    ...defaultChartOptions.value.chart,
    id: 'meshh-chart-zone-visited',
    type: 'treemap',
    toolbar: getChartExportSettings(
      props.widget.name,
      props.area?.name || '',
      props.time?.name || '',
      '',
      props.cluster?.title || '',
      '',
    ),
  },
  title: {
    text: 'Percent of engaged Visits across all zones',
    align: 'center',
    style: {
      fontSize: '14px',
      fontWeight: 'normal',
      fontFamily: 'Outfit',
      color: '#667085',
    },
  },
  colors: mekkoColors,
  plotOptions: {
    treemap: {
      distributed: true,
      enableShades: false,
    },
  },
  tooltip: {
    y: {
      formatter: (val: number) => {
        return formatNumber(val, 2) + '%';
      },
    },
  },
  dataLabels: {
    style: {
      colors: mekkoTextColors,
      fontSize: '12px',
    },
    formatter: function (val: string, op: any) {
      return [`${formatNumber(op.value, 2)}%`, val];
    },
  },
});

const clusterData = computed(() => {
  const chartData = ref<
    {
      x: string;
      y: number;
    }[]
  >([]);

  const clusterId = props.cluster?.id;

  const dataClusters = props.widget.data['Cluster'];
  if (dataClusters && clusterId) {
    const clusterById = dataClusters[clusterId]['Total'];

    Object.keys(clusterById).forEach((key) => {
      let x = '';
      let y = 0;

      const siteData = props.widget.filters.area.find((area) => area.name === 'Site');
      const clusterNames = siteData?.series;
      if (clusterNames) {
        const thisCluster = clusterNames.find((clust) => clust.id === key);
        if (thisCluster) {
          x = thisCluster.title || '';
        }
      }

      y = clusterById[key];

      chartData.value.push({
        x,
        y,
      });
    });
  }

  return [{ data: chartData.value }];
});

const siteData = computed(() => {
  const chartData = ref<
    {
      x: string;
      y: number;
    }[]
  >([]);

  const dataSite = props.widget.data['Site']['Total'];

  if (dataSite) {
    Object.keys(dataSite).forEach((key) => {
      let x = '';
      let y = 0;

      const siteData = props.widget.filters.area.find((area) => area.name === 'Site');
      const clusterNames = siteData?.series;
      if (clusterNames) {
        const thisCluster = clusterNames.find((cluster) => cluster.id === key);
        if (thisCluster) {
          x = thisCluster.title || '';
        }
      }

      y = dataSite[key];

      chartData.value.push({
        x,
        y,
      });
    });
  }

  return [{ data: chartData.value }];
});
</script>

<style lang="scss">
.zone-visited {
  .apexcharts-title-text {
    text-transform: uppercase;
  }

  text {
    tspan:first-of-type {
      font-size: 20px;
      font-weight: bold;
    }
  }

  .chart-wrapper {
    height: calc(100% - 12px);
  }
}
</style>
